import React, { useCallback, useEffect, useState } from 'react';
import type { AgentMember, Team } from '../../../../types';
import { teamService } from '../../services/teamService';

interface AgentManagerProps {
  team: Team;
}

/** 给 agent 的接入提示词：地址、令牌、工作规矩，粘贴给 Claude Code / Cursor 等即可 */
const buildPrompt = (token: string) => `你是我们团队看板（${window.location.origin}）上的 agent 成员。按下面做：

1. 下载命令行：curl -O ${window.location.origin}/agent/agent-cli.mjs （需要 Node 18+）
2. 设置令牌：export TASKTRACK_TOKEN=${token}
   （有 MCP 的话也可用 ${window.location.origin}/agent/agent-mcp.mjs）
3. 领任务：node agent-cli.mjs claim。没有可领的任务就结束，不要自己找事做。
4. 先读描述和完成标准。信息不够或需要人拍板时，不要猜：node agent-cli.mjs block --task <id> --reason "..."
5. 先写一条拆解：node agent-cli.mjs progress --task <id> --kind PLAN --text "..."，然后动手做，关键节点写 PROGRESS。
6. 自己做代码审查并实际验证。
7. 提交验收：node agent-cli.mjs submit --task <id> --summary "改了什么" --review "审查结论" --verification "验证了什么、结果" --unverified "没验到的、需要我亲自过的（没有写无）"
8. 只有人能把任务标为已完成，你到「待验收」为止。

完整接口文档：${window.location.origin}/agent/agent-api.txt`;

const formatTime = (iso?: string | null) =>
  iso ? new Date(iso).toLocaleString('zh-CN', { month: 'numeric', day: 'numeric', hour: '2-digit', minute: '2-digit' }) : '从未使用';

/** 团队所有者/管理员：创建 agent 成员，管理它们的 API 令牌 */
export const AgentManager: React.FC<AgentManagerProps> = ({ team }) => {
  const [agents, setAgents] = useState<AgentMember[]>([]);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // 新生成的令牌明文，只显示这一次
  const [freshToken, setFreshToken] = useState<{ agentName: string; token: string } | null>(null);
  const [copied, setCopied] = useState(false);
  const [promptCopied, setPromptCopied] = useState(false);

  const load = useCallback(async () => {
    try {
      setAgents(await teamService.getAgents(team.id));
    } catch (e) {
      console.error('加载 agent 失败:', e);
      setError('加载 agent 失败');
    }
  }, [team.id]);

  useEffect(() => { load(); }, [load]);

  const run = async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.message || '操作失败，请重试');
    } finally {
      setBusy(false);
    }
  };

  const handleCreate = () => {
    const trimmed = name.trim();
    if (!trimmed) return;
    run(async () => {
      const result = await teamService.createAgent(team.id, trimmed);
      setFreshToken({ agentName: trimmed, token: result.token.token });
      setCopied(false); setPromptCopied(false);
      setName('');
    });
  };

  const handleNewToken = (agent: AgentMember) =>
    run(async () => {
      const result = await teamService.createAgentToken(team.id, agent.id, '新令牌');
      setFreshToken({ agentName: agent.nickname || agent.name, token: result.token });
      setCopied(false); setPromptCopied(false);
    });

  const handleRevoke = (agent: AgentMember, tokenId: string) => {
    if (!window.confirm('吊销后这个令牌立刻失效，正在用它的 agent 会被拒绝。确定吗？')) return;
    run(() => teamService.revokeAgentToken(team.id, agent.id, tokenId));
  };

  const copyPrompt = async () => {
    if (!freshToken) return;
    try {
      await navigator.clipboard.writeText(buildPrompt(freshToken.token));
      setPromptCopied(true);
    } catch {
      setPromptCopied(false);
    }
  };

  const copyToken = async () => {
    if (!freshToken) return;
    try {
      await navigator.clipboard.writeText(freshToken.token);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  return (
    <div className="bg-gray-50 rounded-lg p-3">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold text-gray-800">Agent 成员</h3>
        <span className="text-xs text-gray-400">agent 用令牌调用接口，不能登录，不能把任务标为已完成</span>
      </div>

      {freshToken && (
        <div className="mb-3 p-3 rounded-md border border-amber-300 bg-amber-50">
          <div className="text-xs text-amber-800 mb-1">
            「{freshToken.agentName}」的令牌，<b>只显示这一次</b>，关闭后无法再查看，请现在复制保存：
          </div>
          <code className="block text-xs break-all bg-white border border-amber-200 rounded p-2 select-all">{freshToken.token}</code>
          <div className="mt-2 flex gap-2">
            <button onClick={copyToken} className="px-2 py-1 text-xs rounded bg-amber-500 text-white hover:bg-amber-600">
              {copied ? '已复制' : '复制令牌'}
            </button>
            <button onClick={copyPrompt} className="px-2 py-1 text-xs rounded bg-white border border-amber-300 text-amber-700 hover:bg-amber-50">
              {promptCopied ? '已复制' : '复制接入提示词'}
            </button>
            <button onClick={() => setFreshToken(null)} className="px-2 py-1 text-xs rounded bg-white border border-gray-200 text-gray-600 hover:bg-gray-50">
              我已保存，关闭
            </button>
          </div>
        </div>
      )}

      {agents.length === 0 ? (
        <div className="text-xs text-gray-400 mb-2">还没有 agent 成员</div>
      ) : (
        <ul className="space-y-2 mb-3">
          {agents.map(agent => (
            <li key={agent.id} className="bg-white rounded-md border border-gray-100 p-2">
              <div className="flex items-center justify-between">
                <div className="text-sm text-gray-800">
                  {agent.nickname || agent.name}
                  <span className="ml-1 px-1 rounded bg-indigo-100 text-indigo-700 text-xs">AI</span>
                </div>
                <button
                  onClick={() => handleNewToken(agent)}
                  disabled={busy}
                  className="text-xs text-blue-600 hover:text-blue-700 disabled:opacity-40"
                >
                  生成新令牌
                </button>
              </div>
              <ul className="mt-1 space-y-1">
                {agent.apiTokens.map(t => (
                  <li key={t.id} className="flex items-center justify-between text-xs text-gray-500">
                    <span className={t.revokedAt ? 'line-through text-gray-300' : ''}>
                      {t.prefix}… · {t.name} · 最近使用：{formatTime(t.lastUsedAt)}
                    </span>
                    {t.revokedAt ? (
                      <span className="text-gray-300">已吊销</span>
                    ) : (
                      <button onClick={() => handleRevoke(agent, t.id)} disabled={busy} className="text-red-500 hover:text-red-600 disabled:opacity-40">
                        吊销
                      </button>
                    )}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}

      <div className="flex gap-2">
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
          maxLength={50}
          placeholder="新 agent 的名字，例如 claude-dev"
          className="flex-1 text-sm border border-gray-200 rounded-md px-2 py-1 focus:outline-none focus:ring-2 focus:ring-blue-200"
        />
        <button
          onClick={handleCreate}
          disabled={busy || !name.trim()}
          className="px-3 py-1 text-sm rounded-md bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-40"
        >
          创建
        </button>
      </div>
      {error && <div className="mt-2 text-xs text-red-500">{error}</div>}
    </div>
  );
};
